import { useState, useCallback } from 'react';
import { ChevronRight, ChevronDown, Folder, FileText, FileImage, FileCode, File as FileIcon } from 'lucide-react';

function getIcon(name) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['html', 'htm'].includes(ext)) return <FileCode className="w-4 h-4 text-orange-500 flex-shrink-0" />;
  if (['css', 'scss', 'less'].includes(ext)) return <FileCode className="w-4 h-4 text-blue-500 flex-shrink-0" />;
  if (['js', 'jsx', 'ts', 'tsx'].includes(ext)) return <FileCode className="w-4 h-4 text-yellow-500 flex-shrink-0" />;
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico'].includes(ext)) return <FileImage className="w-4 h-4 text-green-500 flex-shrink-0" />;
  if (['json', 'xml', 'txt', 'md'].includes(ext)) return <FileText className="w-4 h-4 text-zinc-500 flex-shrink-0" />;
  return <FileIcon className="w-4 h-4 text-zinc-400 flex-shrink-0" />;
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function flattenTree(items, depth, expandedSet) {
  const result = [];
  for (const item of items) {
    result.push({ ...item, depth });
    if (item.type === 'folder' && expandedSet.has(item.path) && item.children) {
      result.push(...flattenTree(item.children, depth + 1, expandedSet));
    }
  }
  return result;
}

export default function FileTree({ tree, onSelect, selectedPath }) {
  const [expanded, setExpanded] = useState(() => {
    const set = new Set();
    if (tree) {
      for (const item of tree) {
        if (item.type === 'folder') set.add(item.path);
      }
    }
    return set;
  });

  const toggle = useCallback((path) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  if (!tree || tree.length === 0) {
    return <p className="text-sm text-zinc-400 p-4 font-body">No files</p>;
  }

  const flat = flattenTree(tree, 0, expanded);

  return (
    <div className="py-1" data-testid="file-tree">
      {flat.map((item) => {
        const isFolder = item.type === 'folder';
        const isSelected = selectedPath === item.path;

        return (
          <button
            key={item.path}
            onClick={() => isFolder ? toggle(item.path) : onSelect(item)}
            className={`w-full flex items-center gap-1.5 py-1 px-2 text-left text-sm font-body transition-colors duration-100 ${isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-zinc-100 text-zinc-700'}`}
            style={{ paddingLeft: `${item.depth * 16 + 8}px` }}
            data-testid={`file-tree-item-${item.path}`}
          >
            {isFolder ? (
              expanded.has(item.path) ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
            ) : (
              <span className="w-3.5 flex-shrink-0" />
            )}
            {isFolder ? <Folder className="w-4 h-4 text-zinc-400 flex-shrink-0" /> : getIcon(item.name)}
            <span className="truncate font-mono text-xs">{item.name}</span>
            {!isFolder && item.size != null && (
              <span className="ml-auto text-xs text-zinc-400 flex-shrink-0 pl-2">{formatSize(item.size)}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
